import { useEffect, useRef, type ChangeEvent, type DragEvent } from "react";
import { Icon } from "@common/index";
import { ACCEPTED_IMAGE_TYPES } from "@admin/_helpers/productImageFile";
import type { PickedImage } from "../_helpers/usePickedImages";

interface NewProductImagesProps {
    images: PickedImage[];
    /** One message per file the last add refused. */
    refusals: string[];
    onAdd: (files: File[]) => void;
    onRemove: (previewUrl: string) => void;
    /** Lock the area while the create is in flight. */
    disabled?: boolean;
}

// The Add Product photo area: a drop zone that also opens the file picker,
// then the main image, a thumbnail per image and a "+" tile. Nothing is
// uploaded here; the page sends the files with the create. The create takes
// no order or primary flag, so there is no reorder or set-primary as on the
// Edit page's ProductImageManager: the first image chosen is shown as main.
export const NewProductImages = ({
    images,
    refusals,
    onAdd,
    onRemove,
    disabled = false,
}: NewProductImagesProps) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dropZoneRef = useRef<HTMLButtonElement>(null);
    const removeButtons = useRef(new Map<string, HTMLButtonElement>());
    // Set by a remove, used once the render without that image has landed:
    // the preview URL of the image whose Remove button takes focus, or null
    // for the drop zone. Undefined when no remove is waiting.
    const focusAfterRemove = useRef<string | null | undefined>(undefined);
    const openPicker = () => fileInputRef.current?.click();
    const mainImage = images[0];

    useEffect(() => {
        const target = focusAfterRemove.current;

        if (target === undefined) {
            return;
        }

        focusAfterRemove.current = undefined;

        if (target === null) {
            dropZoneRef.current?.focus();
        } else {
            removeButtons.current.get(target)?.focus();
        }
    }, [images]);

    // The removed button unmounts with its tile, so focus would fall to the
    // page. It goes to the next image's Remove button, else the previous
    // one's; with no image left, the drop zone has replaced the row ("+" tile
    // included), so it goes there.
    const handleRemove = (index: number) => {
        const neighbour = images[index + 1] ?? images[index - 1];

        focusAfterRemove.current = neighbour ? neighbour.previewUrl : null;
        onRemove(images[index].previewUrl);
    };

    const handlePick = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        // Defensive: nothing is added while the create is in flight. A
        // browser's file chooser is modal, so the area should not lock while
        // one is open; this covers a change event that arrives anyway.
        if (!disabled && files && files.length > 0) {
            onAdd(Array.from(files));
        }

        // Allow picking the same file again after removing it.
        event.target.value = "";
    };

    // Prevented on every drag, locked or not, so a file dropped here is never
    // opened by the browser in place of the page.
    const handleDragOver = (event: DragEvent<HTMLDivElement>) =>
        event.preventDefault();

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();

        if (!disabled && event.dataTransfer.files.length > 0) {
            onAdd(Array.from(event.dataTransfer.files));
        }
    };

    return (
        <div onDragOver={handleDragOver} onDrop={handleDrop}>
            {mainImage ? (
                <>
                    <div className="aspect-[3/2] w-full rounded border border-base-300 overflow-hidden flex items-center justify-center">
                        <img
                            src={mainImage.previewUrl}
                            alt={`Main image: ${mainImage.file.name}`}
                            className="h-full w-full object-contain"
                        />
                    </div>

                    <div className="mt-3 flex gap-3 flex-wrap items-center">
                        {images.map((image, index) => (
                            <div
                                key={image.previewUrl}
                                className="relative h-16 w-16 shrink-0 rounded border border-base-300 overflow-hidden"
                            >
                                <img
                                    src={image.previewUrl}
                                    alt={image.file.name}
                                    className="h-full w-full object-cover"
                                />

                                <button
                                    ref={(button) => {
                                        if (button) {
                                            removeButtons.current.set(image.previewUrl, button);
                                        } else {
                                            removeButtons.current.delete(image.previewUrl);
                                        }
                                    }}
                                    type="button"
                                    aria-label={`Remove ${image.file.name}`}
                                    disabled={disabled}
                                    className="absolute top-0 right-0 p-0.5 text-error"
                                    onClick={() => handleRemove(index)}
                                >
                                    <Icon name="close" className="text-base" />
                                </button>
                            </div>
                        ))}

                        <button
                            type="button"
                            aria-label="Add images"
                            disabled={disabled}
                            className="h-16 w-16 shrink-0 rounded border-2 border-dashed border-base-300 flex items-center justify-center text-gray-500"
                            onClick={openPicker}
                        >
                            <Icon name="add" className="text-2xl" />
                        </button>
                    </div>
                </>
            ) : (
                <button
                    ref={dropZoneRef}
                    type="button"
                    disabled={disabled}
                    className="aspect-[3/2] w-full rounded-lg border border-dashed border-base-300 bg-base-200 flex flex-col items-center justify-center text-sm text-gray-500"
                    onClick={openPicker}
                >
                    {/* The spaces keep the button's accessible name readable;
                        the flex column does not render them. */}
                    <span>Drag Photo Here</span>{" "}
                    <span>or</span>{" "}
                    <span>Click to Upload</span>
                </button>
            )}

            <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(",")}
                multiple
                className="hidden"
                aria-label="Upload images"
                disabled={disabled}
                onChange={handlePick}
            />

            {/* A hint only: the API does not check image dimensions. */}
            <p className="mt-2 text-xs text-gray-500">
                Min. Size: 2400px x 2400px
            </p>

            {/* A polite live region, rendered before any refusal so a screen
                reader announces the refusals put into it. */}
            <div role="status">
                {refusals.length > 0 && (
                    <ul className="mt-2 space-y-1 text-sm text-error">
                        {refusals.map((refusal, index) => (
                            <li key={index}>{refusal}</li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};
